import { FC, useState, MouseEvent, useRef, ReactNode, useCallback, useEffect } from 'react';
import cn from 'classnames';
import { createPortal } from 'react-dom';
import { useClickAway } from 'react-use';

import { Button } from 'UIPackage/MainModule/components/Button';

import styles from './Dropdown.module.scss';
import { IDropdownItem } from '../interfaces/IDropdownItem';

interface IDropdownProps {
  disabled?: boolean;
  hideByScroll?: boolean;
  isTopMenu?: boolean;
  items: IDropdownItem[];
  onVisibleChange?: (visible: boolean) => void;
  visible?: boolean;
}

const Dropdown: FC<IDropdownProps> = ({
  children,
  disabled,
  hideByScroll = false,
  isTopMenu = false,
  items,
  onVisibleChange,
  visible,
}) => {
  const body = document.querySelector('body');
  const [isVisible, setIsVisible] = useState(false);
  const [location, setLocation] = useState<{ top: number; right: number } | undefined>(undefined);
  const mainRef = useRef(null);
  const menuRef = useRef(null);

  useClickAway(menuRef, (e: Event) => {
    const currentPath = ((e as unknown) as { path: ReactNode[] })?.path;

    if (isVisible && !currentPath?.find(item => item === mainRef.current)) {
      setIsVisible(false);
    }
  });

  useEffect(() => {
    function onScroll() {
      if (isVisible && hideByScroll) {
        setIsVisible(false);
      }
    }
    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [isVisible, hideByScroll]);

  const handleVisibleChange = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      setIsVisible(!isVisible);
      if (onVisibleChange) {
        onVisibleChange(!isVisible);
      }
      if (!isVisible) {
        const elem = e.target as HTMLElement;

        if (elem && body) {
          const { top, height, x, width } = elem.getBoundingClientRect();
          const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

          setLocation({
            top: top + height + window.scrollY + 5,
            right: window.innerWidth - x - scrollBarWidth - width,
          });
        }
      }
    },
    [body, isVisible, onVisibleChange],
  );

  const hideMenu = useCallback(() => {
    setIsVisible(false);
  }, []);

  return (
    <div className={styles.Dropdown} ref={mainRef}>
      {children ? (
        <span className={styles.DropdownCustomButton} onClick={handleVisibleChange}>
          {children}
        </span>
      ) : (
        <Button
          className={cn(styles.DropdownButton, { [styles.DropdownButtonActive]: isVisible && visible !== false })}
          text="Действия"
          variant="contained"
          id="visibleChangeActionsButton"
          type="button"
          endArrow
          disabled={disabled}
          onClick={handleVisibleChange}
        />
      )}
      {isVisible &&
        body &&
        createPortal(
          <ul
            className={cn(
              styles.Menu,
              { [styles.MenuIsActive]: isVisible && visible !== false },
              { [styles.TopMenuIndex]: isTopMenu },
              { [styles.LowMenuIndex]: !isTopMenu },
            )}
            style={{ top: location?.top, right: location?.right }}
            ref={menuRef}
          >
            {items.map(item => {
              if (item.isDivider) {
                return <li key={item.id} className={styles.MenuDivider} />;
              }

              return (
                <li className={styles.ListItem} key={item.id} onClick={hideMenu}>
                  {item.content}
                  {item.additionalContent && (
                    <ul className={styles.AdditionalMenu}>
                      {item.additionalContent.map(additional => (
                        <li key={additional.id}>{additional.content}</li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>,
          body,
        )}
    </div>
  );
};

export { Dropdown };
